import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Row, Col, Table, Accordion, Offcanvas } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell, Legend } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faShieldHalved,
  faGaugeHigh,
  faCircleCheck,
  faTriangleExclamation,
  faCheck,
  faTimes,
  faFileCode,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import {
  runConfigAnalysisV3,
  fetchLatestConfigAnalysis,
  fetchCSAPlusProject,
  listCSAPlusUploads,
  ConfigAnalysisPerFileResponse,
  EvaluatedRule,
  CSAPlusUploadItem
} from '../data-access/csa-plus.api';
import { setToast } from 'components/Toasts/data-access/toast-reducer';

const TrustGauge = ({ score }: { score: number }) => {
  const color = score >= 80 ? '#25b003' : score >= 50 ? '#ffc107' : '#dc3545';
  const label = score >= 80 ? 'Strong' : score >= 50 ? 'Fair' : 'Weak';
  return (
    <div className="text-center">
      <div
        style={{
          width: 120,
          height: 120,
          borderRadius: '50%',
          border: `8px solid ${color}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 8px'
        }}
      >
        <span style={{ fontSize: 32, fontWeight: 700, color }}>{score}</span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>/ 100</span>
      </div>
      <span className="badge" style={{ backgroundColor: color, fontSize: 12 }}>
        {label}
      </span>
    </div>
  );
};

const SummaryCard = ({
  icon,
  label,
  value,
  color
}: {
  icon: any;
  label: string;
  value: string | number;
  color: string;
}) => (
  <div className="rounded-3 border p-3 d-flex align-items-center gap-3" style={{ backgroundColor: '#fafafa' }}>
    <div
      className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
      style={{ width: 40, height: 40, backgroundColor: `${color}18` }}
    >
      <FontAwesomeIcon icon={icon} style={{ color, fontSize: 18 }} />
    </div>
    <div>
      <p className="mb-0 text-muted small">{label}</p>
      <p className="mb-0 fw-bold" style={{ fontSize: 20 }}>
        {value}
      </p>
    </div>
  </div>
);

const CoverageAnalysisTab = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = useState(false);
  const [perFileResponse, setPerFileResponse] = useState<ConfigAnalysisPerFileResponse | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [isLoadedFromCache, setIsLoadedFromCache] = useState(false);
  const [hasPendingPolicies, setHasPendingPolicies] = useState(false);
  const [configUploads, setConfigUploads] = useState<CSAPlusUploadItem[]>([]);
  const [selectedConfigIds, setSelectedConfigIds] = useState<Set<string>>(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Derived result for the selected file
  const result = selectedFileId ? perFileResponse?.per_file_results?.[selectedFileId]?.result ?? null : null;

  useEffect(() => {
    const loadSavedAndCheckStatus = async () => {
      if (!projectId) return;
      try {
        const [saved, project] = await Promise.all([
          fetchLatestConfigAnalysis(projectId),
          fetchCSAPlusProject(projectId)
        ]);

        if (saved) {
          setPerFileResponse(saved);
          setIsLoadedFromCache(true);
          const fileRows =
            saved.files?.length > 0
              ? saved.files
              : Object.entries(saved.per_file_results ?? {}).map(([id, ent]) => ({
                  upload_id: ent.upload_id ?? id,
                  filename: ent.filename
                }));
          if (fileRows.length > 0) {
            setSelectedFileId(String(fileRows[0].upload_id));
          }
        }

        const uploads = listCSAPlusUploads(project?.uploads);
        const pending = uploads.some(u => u.doc_type === 'policy' && !['extracted', 'indexed'].includes(u.status));
        setHasPendingPolicies(pending);

        const configs = uploads.filter(u => u.doc_type === 'config' && u.upload_id);
        setConfigUploads(configs);
        setSelectedConfigIds(new Set(configs.map(u => u.upload_id)));
      } catch (err) {
        console.error('Failed to load saved config analysis and checks:', err);
      }
    };
    loadSavedAndCheckStatus();
  }, [projectId]);

  const handleProcess = async () => {
    if (!projectId) return;
    setIsProcessing(true);
    setIsLoadedFromCache(false);
    try {
      // Separate uploads by doc_type so V3 endpoint knows which are policies vs configs
      const project = await fetchCSAPlusProject(projectId);
      const uploads = listCSAPlusUploads(project?.uploads);
      const policyUploadIds = uploads.filter(u => u.doc_type === 'policy').map(u => u.upload_id);
      const configUploadIds = uploads
        .filter(u => u.doc_type === 'config' && selectedConfigIds.has(u.upload_id))
        .map(u => u.upload_id);

      if (configUploadIds.length === 0) {
        dispatch(
          setToast({
            show: true,
            title: 'No config files',
            message: 'Upload at least one config file before running analysis.'
          })
        );
        return;
      }

      // Trigger V3 run (async 202) then poll until results appear
      const data = await runConfigAnalysisV3({
        projectId,
        policyUploadIds,
        configUploadIds
      });

      setPerFileResponse(data);
      const fileRows =
        data.files?.length > 0
          ? data.files
          : Object.entries(data.per_file_results ?? {}).map(([id, ent]) => ({
              upload_id: ent.upload_id ?? id,
              filename: ent.filename
            }));
      if (fileRows.length > 0) {
        setSelectedFileId(String(fileRows[0].upload_id));
      }

      const scoresSummary = fileRows
        .map(f => {
          const score = data.per_file_results[f.upload_id]?.result?.summary_stats?.trust_score ?? 0;
          return `${f.filename}: ${score}`;
        })
        .join(' | ');

      dispatch(
        setToast({
          show: true,
          title: 'Config Analysis complete',
          message: `Processed ${fileRows.length} file(s). Trust Scores — ${scoresSummary}`
        })
      );
    } catch (err: any) {
      dispatch(
        setToast({
          show: true,
          title: 'Analysis failed',
          message: err?.response?.data?.error || err?.message || 'Configuration analysis failed.'
        })
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const plotData = result?.plot_data;
  const summary = result?.summary_stats;

  // Mocked/Derived Data Formats expected from PlotData
  // These will gracefully fall back to empty arrays if backend isn't returning them yet
  const categoryScores = (plotData?.category_scores ?? []).map((item: any) => ({
    name: item.category,
    score: item.score
  }));

  const controlScores = (plotData?.control_scores ?? []).map((item: any) => ({
    name: item.control_id,
    compliance: item.compliance_percentage
  }));

  const complianceComparison = (plotData?.compliance_comparison ?? []).map((item: any) => ({
    subject: item.framework,
    A: item.internal_score,
    B: item.benchmark_score,
    fullMark: 100
  }));

  const ruleSettings = result?.evaluated_rules ?? [];

  return (
    <div>
      {/* Header row */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h6 className="fw-bold mb-1 text-body">Coverage Analysis (Configs &amp; Policies)</h6>
          <p className="text-muted small mb-0">
            Run a deterministic compliance evaluation of uploaded config files against extracted policy rules.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {hasPendingPolicies && (
            <span className="badge bg-warning-subtle text-warning border px-2 py-1 small" style={{ fontSize: '12px' }}>
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
              Rule Extraction Pending
            </span>
          )}
          <button
            type="button"
            className="btn d-flex align-items-center gap-2 px-4 py-2"
            style={{
              backgroundColor: hasPendingPolicies ? '#6c757d' : '#25b003',
              color: '#fff',
              borderRadius: 8,
              fontWeight: 500,
              opacity: hasPendingPolicies ? 0.65 : 1
            }}
            onClick={handleProcess}
            disabled={isProcessing || hasPendingPolicies}
            title={hasPendingPolicies ? 'Wait until all policy rules are extracted to analyze' : ''}
          >
            {isProcessing ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                Evaluating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlay} />
                Evaluate Configs
              </>
            )}
          </button>
        </div>
      </div>

      {configUploads.length > 0 && (
        <div className="mb-4 d-flex align-items-center gap-2">
          <span
            className="border rounded-pill px-3 py-1 small fw-500"
            style={{ borderColor: '#3874ff', color: '#3874ff', fontSize: '13px', fontWeight: 500 }}
          >
            <FontAwesomeIcon icon={faFileCode} className="me-2" style={{ fontSize: 12 }} />
            Config Files: {configUploads.length}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            style={{
              backgroundColor: '#3874ff',
              color: '#fff',
              borderRadius: 6,
              fontWeight: 500,
              fontSize: '13px',
              padding: '4px 16px',
              boxShadow: '0 2px 4px rgba(56,116,255,0.25)'
            }}
            onClick={() => setDrawerOpen(true)}
          >
            View All
          </button>
        </div>
      )}

      {/* Config File Drawer */}
      <Offcanvas show={drawerOpen} onHide={() => setDrawerOpen(false)} placement="end" style={{ width: 360 }}>
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Drawer Header */}
          <div
            className="d-flex align-items-center justify-content-between px-4 py-3"
            style={{ borderBottom: '1px solid #e5e7eb' }}
          >
            <span style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>Config Files</span>
            <button
              type="button"
              className="btn btn-sm btn-light"
              style={{ borderRadius: 6, padding: '4px 8px' }}
              onClick={() => setDrawerOpen(false)}
            >
              <FontAwesomeIcon icon={faXmark} style={{ color: '#6b7280' }} />
            </button>
          </div>

          {/* Drawer File List */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {configUploads.map((u, index) => {
              const isSelected = selectedFileId === u.upload_id;
              const hasResult = !!perFileResponse?.per_file_results?.[u.upload_id];
              const trustScore = perFileResponse?.per_file_results?.[u.upload_id]?.result?.summary_stats?.trust_score;
              const isChecked = selectedConfigIds.has(u.upload_id);

              return (
                <div key={u.upload_id}>
                  <div
                    className="px-4 py-3 d-flex align-items-start gap-3"
                    style={{
                      cursor: hasResult ? 'pointer' : 'default',
                      position: 'relative',
                      backgroundColor: isSelected && hasResult ? '#f0f4ff' : 'transparent',
                      opacity: hasResult ? 1 : 0.55,
                      transition: 'background-color 0.15s'
                    }}
                    onClick={() => {
                      if (hasResult) {
                        setSelectedFileId(u.upload_id);
                        setDrawerOpen(false);
                      }
                    }}
                    onMouseEnter={e => {
                      if (hasResult && !isSelected)
                        (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f0f4ff';
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* Timeline dot + line */}
                    <div
                      style={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        flexShrink: 0,
                        marginTop: 2
                      }}
                    >
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: isSelected ? '#3874ff' : hasResult ? '#305d84' : '#d1d5db',
                          border: '2px solid white',
                          boxShadow: isSelected ? '0 0 0 2px #3874ff40' : '0 0 0 1px rgba(0,0,0,0.1)',
                          zIndex: 1,
                          flexShrink: 0
                        }}
                      />
                      {index < configUploads.length - 1 && (
                        <div style={{ width: 2, flex: 1, minHeight: 40, backgroundColor: '#e5e7eb', marginTop: 2 }} />
                      )}
                    </div>

                    {/* File info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <span style={{ fontWeight: 600, fontSize: '13px', color: '#111827', wordBreak: 'break-word' }}>
                          {u.original_filename}
                        </span>
                        {isSelected && hasResult && (
                          <span
                            className="badge"
                            style={{
                              backgroundColor: '#25b003',
                              fontSize: '11px',
                              fontWeight: 500,
                              padding: '2px 8px',
                              borderRadius: 20
                            }}
                          >
                            Viewing
                          </span>
                        )}
                      </div>
                      {u.uploaded_at && (
                        <div className="mb-1" style={{ fontSize: '11px', color: '#9ca3af' }}>
                          Uploaded {new Date(u.uploaded_at).toLocaleString()}
                        </div>
                      )}
                      {hasResult && trustScore !== undefined ? (
                        <div className="mb-1" style={{ fontSize: '12px', color: '#6b7280' }}>
                          Trust Score:{' '}
                          <strong
                            style={{ color: trustScore >= 80 ? '#25b003' : trustScore >= 50 ? '#ffc107' : '#dc3545' }}
                          >
                            {trustScore}
                          </strong>
                        </div>
                      ) : !hasResult ? (
                        <div className="mb-1" style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>
                          No results yet — check the box below and run evaluation
                        </div>
                      ) : null}
                      {/* Checkbox for pre-run selection */}
                      <label
                        className="d-flex align-items-center gap-2 mt-1 mb-0"
                        style={{ cursor: 'pointer', fontSize: '12px', color: '#6b7280', userSelect: 'none' }}
                        onClick={e => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={e => {
                            const next = new Set(selectedConfigIds);
                            e.target.checked ? next.add(u.upload_id) : next.delete(u.upload_id);
                            setSelectedConfigIds(next);
                          }}
                        />
                        Include in evaluation
                      </label>
                    </div>
                  </div>
                  {index < configUploads.length - 1 && (
                    <div style={{ borderBottom: '1px solid #f3f4f6', marginLeft: 48 }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Offcanvas>

      {isLoadedFromCache && result && !isProcessing && (
        <div className="alert alert-light border shadow-sm d-flex align-items-center justify-content-between mb-4 p-3 rounded-2">
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faShieldHalved} className="text-muted" />
            <span className="small text-body-secondary">
              Showing last saved result for{' '}
              <strong>{perFileResponse?.per_file_results[selectedFileId!]?.filename}</strong>. Click{' '}
              <strong>Evaluate Configs</strong> to refresh all.
            </span>
          </div>
        </div>
      )}

      {!result && !isProcessing && (
        <div className="text-center py-5 rounded-3 border" style={{ backgroundColor: '#fafafa', color: '#9ca3af' }}>
          <FontAwesomeIcon icon={faShieldHalved} size="2x" className="mb-3 opacity-50" />
          <p className="mb-0 small">
            Click <strong>Evaluate Configs</strong> to run mapping and semantic analysis via LLM.
          </p>
        </div>
      )}

      {isProcessing && (
        <div className="text-center py-5">
          <div className="spinner-border text-success mb-3" role="status" />
          <p className="text-muted small">Running LLM-assisted vector retrieval and rule evaluation…</p>
        </div>
      )}

      {result && !isProcessing && summary && (
        <>
          {/* Summary Row */}
          <Row className="g-3 mb-4">
            <Col xs={12} md={3}>
              <div
                className="rounded-3 border p-3 d-flex flex-column align-items-center justify-content-center h-100"
                style={{ backgroundColor: '#fafafa' }}
              >
                <p className="text-muted small mb-2 fw-semibold">Config Trust Score</p>
                <TrustGauge score={Math.round(summary.trust_score ?? 0)} />
              </div>
            </Col>
            <Col xs={12} md={9}>
              <Row className="g-3 h-100">
                <Col xs={6} md={4}>
                  <div
                    className="rounded-3 border p-3 d-flex align-items-center gap-3 h-100"
                    style={{ backgroundColor: '#fafafa' }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                      style={{ width: 40, height: 40, backgroundColor: '#305d8418' }}
                    >
                      <FontAwesomeIcon icon={faGaugeHigh} style={{ color: '#305d84', fontSize: 18 }} />
                    </div>
                    <div>
                      <p className="mb-0 text-muted small">Rules Applicable</p>
                      <p className="mb-0 fw-bold" style={{ fontSize: 20 }}>
                        {(() => {
                          const total = summary.total_rules_evaluated ?? 0;
                          const unchecked = summary.unchecked_count ?? 0;
                          const applicable = total - unchecked;
                          return `${applicable} / ${total}`;
                        })()}
                      </p>
                    </div>
                  </div>
                </Col>
                <Col xs={6} md={4}>
                  <SummaryCard
                    icon={faCircleCheck}
                    label="Passed"
                    value={summary.compliant_count ?? 0}
                    color="#25b003"
                  />
                </Col>
                <Col xs={6} md={4}>
                  <SummaryCard
                    icon={faTriangleExclamation}
                    label="Failed"
                    value={summary.non_compliant_count ?? 0}
                    color="#dc3545"
                  />
                </Col>
                <Col xs={12} md={12}>
                  <div className="rounded-3 border p-3 bg-white h-100 d-flex flex-column justify-content-center">
                    <p className="text-muted small fw-bold mb-1">Executive Summary</p>
                    <p className="mb-0" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                      {summary.executive_summary || 'Auto-generated summary from LLM analysis.'}
                    </p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Charts Row 1: Category Bar & Control Bar */}
          <Row className="g-4 mb-4">
            <Col xs={12} md={controlScores.length > 0 ? 6 : 12}>
              <div className="rounded-3 border p-4 bg-white h-100">
                <h6 className="fw-semibold mb-3 text-body">Category-Wise Security Score</h6>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={categoryScores} margin={{ top: 5, right: 10, left: -20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" />
                    <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                    <Tooltip cursor={{ fill: 'transparent' }} />
                    <Bar dataKey="score" name="Score" fill="#3874ff" radius={[4, 4, 0, 0]}>
                      {categoryScores.map((entry: any, index: number) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.score >= 80 ? '#25b003' : entry.score >= 50 ? '#ffc107' : '#dc3545'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
            {controlScores.length > 0 && (
              <Col xs={12} md={6}>
                <div className="rounded-3 border p-4 bg-white h-100">
                  <h6 className="fw-semibold mb-3 text-body">Control-Wise Compliance Score</h6>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={controlScores} margin={{ top: 5, right: 10, left: -20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" />
                      <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                      <Tooltip cursor={{ fill: 'transparent' }} />
                      <Bar dataKey="compliance" name="Compliance %" fill="#6f42c1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Col>
            )}
          </Row>

          {/* Charts Row 2: Radar Comparison & Scatter Heatmap */}
          {complianceComparison.length > 0 && (
            <Row className="g-4 mb-4">
              <Col xs={12} md={12}>
                <div className="rounded-3 border p-4 bg-white h-100">
                  <h6 className="fw-semibold mb-3 text-body">Internal vs. Benchmark Compliance</h6>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={complianceComparison} margin={{ top: 5, right: 10, left: -20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="subject" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                      <Tooltip cursor={{ fill: 'transparent' }} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Bar dataKey="A" name="Internal Policy" fill="#3874ff" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="B" name="CIS Benchmark" fill="#25b003" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Col>
            </Row>
          )}

          {/* Grouped Findings Accordion */}
          {ruleSettings.length > 0 &&
            (() => {
              const groups = {
                fail: ruleSettings.filter((r: EvaluatedRule) => r.status?.toLowerCase() === 'fail'),
                pass: ruleSettings.filter((r: EvaluatedRule) => r.status?.toLowerCase() === 'pass'),
                unchecked: ruleSettings.filter((r: EvaluatedRule) => r.status?.toLowerCase() === 'unchecked')
              };

              const groupConfig = [
                {
                  key: 'fail',
                  label: 'Failed Findings',
                  badge: 'bg-danger',
                  icon: faTimes,
                  color: '#dc3545',
                  defaultOpen: true
                },
                {
                  key: 'pass',
                  label: 'Passed Settings',
                  badge: 'bg-success',
                  icon: faCheck,
                  color: '#25b003',
                  defaultOpen: false
                }
              ] as const;

              return (
                <div className="rounded-3 border p-4 bg-white mb-4 shadow-sm">
                  <h6 className="fw-bold mb-4 text-body d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faShieldHalved} className="text-primary" />
                    Detailed Setting Evaluations
                    <span className="badge bg-primary ms-1">{ruleSettings.length}</span>
                  </h6>

                  <Accordion defaultActiveKey="fail">
                    {groupConfig.map(({ key, label, badge, icon, color }) => {
                      const groupItems = groups[key];
                      if (groupItems.length === 0) return null;

                      return (
                        <Accordion.Item
                          eventKey={key}
                          key={key}
                          className="mb-3 border rounded-2 shadow-sm overflow-hidden"
                        >
                          <Accordion.Header>
                            <div className="d-flex align-items-center gap-3 w-100 pe-3">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle"
                                style={{ width: 28, height: 28, backgroundColor: `${color}15` }}
                              >
                                <FontAwesomeIcon icon={icon} style={{ color, fontSize: 14 }} />
                              </div>
                              <span className="fw-bold text-dark">{label}</span>
                              <span className={`badge ${badge} ms-auto px-3 rounded-pill`} style={{ fontSize: '11px' }}>
                                {groupItems.length} items
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body className="p-0">
                            <Table hover responsive className="mb-0 align-middle" style={{ fontSize: '13px' }}>
                              <thead className="bg-light text-muted fw-bold">
                                <tr>
                                  <th className="ps-3 border-0 py-3" style={{ width: '25%' }}>
                                    SETTING NAME
                                  </th>
                                  <th className="border-0 py-3" style={{ width: '15%' }}>
                                    CATEGORY
                                  </th>
                                  <th className="border-0 py-3" style={{ width: '20%' }}>
                                    ACTUAL VALUE
                                  </th>
                                  <th className="border-0 py-3" style={{ width: '15%' }}>
                                    EXPECTED
                                  </th>
                                  {key === 'fail' && (
                                    <th className="border-0 py-3" style={{ width: '25%' }}>
                                      EXPLANATION & REMEDIATION
                                    </th>
                                  )}
                                  <th className="border-0 py-3 text-end pe-3">FRAMEWORK</th>
                                </tr>
                              </thead>
                              <tbody>
                                {groupItems.map((rule: EvaluatedRule, i: number) => (
                                  <tr key={i} className="border-top">
                                    <td className="ps-3 fw-semibold text-dark">
                                      <div
                                        style={{
                                          maxHeight: '80px',
                                          overflowY: 'auto',
                                          wordBreak: 'break-word',
                                          fontSize: '13px'
                                        }}
                                      >
                                        {rule.setting_name}
                                      </div>
                                    </td>
                                    <td style={{ minWidth: '100px' }}>
                                      <span className="badge bg-light text-dark border font-weight-normal">
                                        {rule.category || 'General'}
                                      </span>
                                    </td>
                                    <td className="font-monospace text-muted">
                                      <div
                                        style={{
                                          maxHeight: '80px',
                                          overflowY: 'auto',
                                          wordBreak: 'break-all',
                                          fontSize: '12px',
                                          color: '#005cc5'
                                        }}
                                      >
                                        {String(rule.actual_value ?? '—')}
                                      </div>
                                    </td>
                                    <td className="font-monospace text-primary fw-medium">
                                      <div style={{ maxHeight: '80px', overflowY: 'auto', wordBreak: 'break-word' }}>
                                        {rule.operator || '='} {String(rule.expected_value ?? '—')}
                                      </div>
                                    </td>
                                    {key === 'fail' && (
                                      <td className="py-3">
                                        <div style={{ maxHeight: '150px', overflowY: 'auto', wordBreak: 'break-word' }}>
                                          <div className="text-danger fw-medium mb-1">{rule.explanation}</div>
                                          {rule.remediation_step && (
                                            <div className="bg-warning-subtle p-2 rounded border-start border-warning border-3 mt-2">
                                              <div className="text-warning-emphasis fw-bold small mb-1">
                                                <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
                                                REMEDIATION
                                              </div>
                                              <div className="text-muted small" style={{ overflowWrap: 'break-word' }}>
                                                {rule.remediation_step}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    )}
                                    <td className="text-end pe-3 text-muted small">
                                      <div className="text-truncate" title={rule.framework_citation}>
                                        {rule.framework_citation || '—'}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                </div>
              );
            })()}
        </>
      )}
    </div>
  );
};

export default CoverageAnalysisTab;
